/**
 * Where the candidate is in the onboarding funnel (`pages/Onboarding.tsx`):
 * reason → consent → CV → conversation → profile.
 *
 * Five numbered markers, no percentage. The interview in the middle runs for as
 * many questions as it takes, so a bar filling to 80% would be a number we made
 * up — and inventing progress is the same padding the product refuses
 * everywhere else (constitution §I). Instead the interview step says plainly
 * that it is a conversation of unknown length, which is the honest answer to
 * "how much is left".
 *
 * Distinct from `OnboardingProgress`, which is the cold-start *wait* at the very
 * end. This is the map; that is a clock.
 *
 * `showBackLink` (PIVOT-145): true only for a candidate with
 * `user.profile_complete` — i.e. someone who arrived via Profile's "redo the
 * interview", not a first-time flow. `/onboarding` renders with no nav of its
 * own, so without this there was no way back to `/profile` short of a
 * hard-refresh; this is the escape half of that ticket's two-step redo (the
 * confirm half lives in `Profile.tsx`).
 */

import { Link } from 'react-router'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import './OnboardingSteps.css'

/**
 * The candidate-visible steps, in order. Keys are written out rather than built
 * as `onboarding.step${name}` so `TranslationKey` can still check them (§6).
 */
const STEPS = [
  { key: 'intent', index: '01', labelKey: 'onboarding.stepIntent' },
  { key: 'consent', index: '02', labelKey: 'onboarding.stepConsent' },
  { key: 'upload', index: '03', labelKey: 'onboarding.stepUpload' },
  { key: 'interview', index: '04', labelKey: 'onboarding.stepInterview' },
  { key: 'review', index: '05', labelKey: 'onboarding.stepReview' },
] as const satisfies readonly { key: string; index: string; labelKey: TranslationKey }[]

/** The five markers this shows. The page maps its own step machine onto these. */
export type OnboardingStepKey = (typeof STEPS)[number]['key']

interface OnboardingStepsProps {
  current: OnboardingStepKey
  /** Show the "back to my profile" link (PIVOT-145). See the module docstring. */
  showBackLink?: boolean
}

export function OnboardingSteps({ current, showBackLink }: OnboardingStepsProps) {
  const { t } = useI18n()
  const currentIndex = STEPS.findIndex((step) => step.key === current)
  const currentStep = STEPS[currentIndex]

  return (
    <nav className="onboarding-steps" aria-label={t('a11y.onboardingSteps')}>
      {showBackLink && (
        <Link to="/profile" className="onboarding-steps-back">
          {t('onboarding.backToProfile')}
        </Link>
      )}
      <ol className="onboarding-steps-list">
        {STEPS.map((step, index) => {
          const state = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'todo'
          return (
            <li
              key={step.key}
              className="onboarding-steps-node"
              data-state={state}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span className="onboarding-steps-index">{step.index}</span>
              <span className="onboarding-steps-label">{t(step.labelKey)}</span>
            </li>
          )
        })}
      </ol>
      {/* PIVOT-143: the one <h1> in the onboarding flow. `AppShell` drops its
          normal header for onboarding (see its own docstring), so nothing
          else on screen names the step until this — every other heading in
          the flow (`IntentSelect`'s `h2`, `CvUpload`'s, ...) is legitimately
          subordinate to it. Rendered as its own line rather than promoting
          the grid's per-node label in place, because that label is pinned to
          a 1/5-width column: at <=640px an unbroken word (French's "La
          conversation") has nowhere to wrap and pushes past the neighbouring
          segment's gutter. A full-width line below the rule can't do that
          regardless of locale — "what it actually communicates on a phone"
          per the ticket, kept unconditionally rather than only under 640px
          so there is exactly one <h1> at every width, not two swapped by
          media query. */}
      {currentStep && (
        <h1 className="onboarding-steps-current">
          {currentStep.index} · {t(currentStep.labelKey)}
        </h1>
      )}
      {current === 'interview' && (
        <p className="onboarding-steps-note">{t('onboarding.stepsInterviewNote')}</p>
      )}
    </nav>
  )
}
